import React from 'react'
import {useSubscription} from '@/shared/utils'
import {useLayoutPanels} from '@/shared/context/ChatContext'
import {useFilesPanelAccess} from '@/app/modules/sharing'

/**
 * The header's layout cluster: one button per lateral panel, grouped so it reads as "the shape of
 * my workspace" rather than as more navigation. Panels are a layout state, not a destination, so
 * they must not live in `HorizontalMenuBar`'s nav — see the union that used to be there.
 *
 * Each glyph is a picture of the result: a frame with the relevant edge filled when that panel is
 * open, a dashed seam where it would appear when it is closed. State is therefore readable without
 * relying on colour alone.
 */

const MOD_KEY =
  typeof navigator !== 'undefined' &&
  /Mac|iP(hone|ad|od)/.test(navigator.platform || navigator.userAgent)
    ? '⌘'
    : 'Ctrl+'

const DockGlyph: React.FC<{side: 'left' | 'right'; open: boolean}> = ({side, open}) => {
  // Mirrored about the 16-unit box's centre so the pair reads as one symmetric set.
  const stripX = side === 'left' ? 2.6 : 8.4
  const seamX = side === 'left' ? 6.4 : 9.6

  return (
    <svg viewBox='0 0 16 16' width='16' height='16' aria-hidden='true' focusable='false'>
      <rect
        x='1.5'
        y='2.5'
        width='13'
        height='11'
        rx='1.6'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.2'
      />
      {open ? (
        <rect x={stripX} y='3.6' width='5' height='8.8' rx='0.6' fill='currentColor' />
      ) : (
        <line
          x1={seamX}
          y1='3.6'
          x2={seamX}
          y2='12.4'
          stroke='currentColor'
          strokeWidth='1.2'
          strokeDasharray='2 2'
          opacity='0.65'
        />
      )}
    </svg>
  )
}

type PanelToggle = {
  id: string
  label: string
  side: 'left' | 'right'
  shortcut: string
  isOpen: boolean
  onToggle: () => void
}

export const LayoutPanelToggles: React.FC = () => {
  const {hasFileAccess, canViewAiChat} = useSubscription()
  const filesPanel = useFilesPanelAccess(hasFileAccess)
  const {isFilesPanelOpen, toggleFilesPanel, isChatVisible, toggleChat} = useLayoutPanels()

  const allToggles: PanelToggle[] = [
    {
      id: 'files',
      label: 'Files',
      side: 'left',
      shortcut: `${MOD_KEY}B`,
      isOpen: isFilesPanelOpen,
      onToggle: toggleFilesPanel,
    },
    {
      id: 'chat',
      label: 'AI Chat',
      side: 'right',
      shortcut: `${MOD_KEY}J`,
      isOpen: isChatVisible,
      onToggle: toggleChat,
    },
  ]

  const toggles = allToggles.filter((toggle) => {
    // The Files panel renders nothing without a subscription or a folder shared with the user (see
    // FilesPanelSlot), so its toggle would be a dead control. The chat toggle needs a subscription
    // that licenses ENGINE (an expired one still opens the chat read-only).
    if (toggle.id === 'files') {
      return filesPanel.visible
    }
    return canViewAiChat
  })

  if (toggles.length === 0) {
    return null
  }

  return (
    <div className='ob-menu-bar__layout-toggles' role='group' aria-label='Workspace panels'>
      {toggles.map((toggle) => {
        // These buttons carry no text, so one string serves as both the tooltip and the
        // accessible name — no hidden-text span to keep in sync with a CSS helper class.
        const description = `${toggle.isOpen ? 'Hide' : 'Show'} ${toggle.label} (${
          toggle.shortcut
        })`

        return (
          <button
            key={toggle.id}
            type='button'
            data-testid={`panel-toggle-${toggle.id}`}
            className={`ob-panel-toggle ${toggle.isOpen ? 'ob-panel-toggle--on' : ''}`}
            aria-pressed={toggle.isOpen}
            aria-label={description}
            title={description}
            onClick={toggle.onToggle}
          >
            <DockGlyph side={toggle.side} open={toggle.isOpen} />
          </button>
        )
      })}
    </div>
  )
}
